<template>
  <div
    class="custom-scrollbar tournament-grid-players-table"
    :class="{ 'is-edit': canEdit, 'block-loading block-loading-bg': isLoading }"
  >
    <HeraDummyList v-if="!players.length" icon-name="group" position="center" />
    <SortTable
      v-else
      ref="sortTable"
      :columns="columns"
      :rows="rows"
      class="hera-table-transparent"
      :class="{ 'cursor-pointer': canEdit }"
      sort-in-component
      :sort-is-disabled="canEdit"
      header-position="pseudo-fixed"
      :listeners="{
        th: {
          mousedown: onFocus,
        },
      }"
      @sort="saveLastSort"
    >
      <template v-if="canEditPlayers" slot="tbodyHelperRow">
        <tr>
          <td></td>
          <td colspan="4" class="text-left">
            <Panel
              :variant="['transparent']"
              class="position-relative p-0 mt-2 d-inline-flex align-items-center"
              @click.stop="onAddPlayer"
              ><Icon
                key="add"
                name="add"
                class="player-add-trigger flex-shrink-0 cursor-pointer"
                original
                :fill="$scss.colors.greenBg"
              />
              <TextBlock variant="success" class="h-ml-3 cursor-pointer">{{
                $t('_web_player_item_add_player', 'Add player')
              }}</TextBlock></Panel
            >
          </td>
        </tr>
      </template>
      <template slot="rank" slot-scope="{ data, row }">
        <InputPlainText
          v-if="canEdit"
          v-model.number="row.rank"
          :class="{
            changed: isChanged(row, 'rank'),
            doubling: isDoubled(row, 'rank'),
            'has-error': isErrorRank(row),
          }"
          :input-props="inputProps({ max: rows.length })"
          @keypress.enter="focusToNextInput"
          @input="onChange(row, 'rank', $event)"
          @focus="onFocus(row, $event)"
        />
        <div v-else class="px-2 text-center">
          {{ data !== null ? data : '-' }}
          <InfoTooltip
            v-if="isDoubled(row, 'rank')"
            class="d-inline-block ml-2"
            :float="['left']"
            :icon-props="{
              name: 'scales',
              fill: $scss.errorColor,
              width: 14,
              class: 'pb-2 cursor-pointer',
            }"
          >
            <TextBlock slot="content" tag="div" class="p-3 text-center">
              <div class="mb-2 text-warning">
                {{ getDoubledRankPlayersNames(data) }}
              </div>
              {{ $$t('were provided equal game-rank results', 'equal-result') }}
            </TextBlock>
          </InfoTooltip>
        </div>
      </template>
      <ListItemAccount
        slot="participant"
        slot-scope="{ data, rowIndex }"
        class="participant-wrapper text-left justify-content-start"
        :secondary-model="data"
      >
        <div slot="action" class="flex-shrink-0 h-ml-auto actions">
          <Icon
            v-if="data && data.checked_in"
            class="h-ml-3"
            name="check-mark"
            :fill="$scss.colors.greenBg"
            @click.stop
          />
          <Icon
            class="cursor-pointer h-ml-3"
            name="chat"
            :fill="$scss.secondaryTextBg"
            @click.stop="$emit('openChat', data)"
          />
          <Icon
            v-if="canEditPlayers"
            class="cursor-pointer h-ml-4"
            name="cancel"
            :fill="$scss.colors.errorBg"
            @click.stop="onDeletePlayer(data.id || data.login, rowIndex)"
          />
        </div>
      </ListItemAccount>
      <template slot="kills" slot-scope="{ data, row }">
        <InputPlainText
          v-if="canEdit"
          v-model.number="row.kills"
          :class="{
            changed: isChanged(row, 'kills'),
          }"
          :input-props="inputProps({ min: 0 })"
          @keypress.enter="focusToNextInput"
          @input="onChange(row, 'kills', $event)"
          @focus="onFocus(row, $event)"
        />
        <div v-else class="px-2 text-center">
          {{ data !== null ? data : '-' }}
        </div>
      </template>
      <template slot="attachments" slot-scope="{ row }">
        <Attachments
          :value="{ attachments: row.attachments }"
          class="d-flex align-items-center justify-content-start"
        />
      </template>
    </SortTable>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

import SortTable from '@/components/molecules/SortTable';
import { prop, propRequired } from '@/utils/factories';
import { translate } from '@/mixins/translate';
import InputPlainText from '@/components/atoms/form/InputPlainText';
import HeraDummyList from '@/components/common/HeraDummyList';
import Icon from '@/components/atoms/Icon';
import Panel from '@/components/atoms/Panel';
import InfoTooltip from '@/components/molecules/InfoTooltip';
import TextBlock from '@/components/atoms/TextBlock';
import ListItemAccount from '@/components/common/ListItemAccount';
import Attachments from '@/components/tournaments/MatchAttachments.vue';
let tabindex = 100; // first tabindex

export default {
  name: 'PlayerTable',
  components: {
    ListItemAccount,
    TextBlock,
    InfoTooltip,
    Icon,
    HeraDummyList,
    InputPlainText,
    SortTable,
    Attachments,
    Panel,
  },
  mixins: [translate('_web_tournament-grid_battle-royale_table')],
  props: {
    players: propRequired(Array),
    canEdit: prop(Boolean),
    canEditPlayers: prop(Boolean),
    isLoading: prop(Boolean),
    ignoreScores: prop(Boolean),
    editResultsData: prop(Object, null),
  },
  data() {
    return {
      rowsBuff: this.prepareRows(this.players),
      activeRowId: null,
      results: null,
      sortBy: null,
      sortOrder: null,
    };
  },
  computed: {
    ...mapGetters(['currentTournamentStage']),
    columns() {
      return [
        {
          id: 'rank',
          title: this.$$t('rank'),
          width: '3.75rem',
          defaultValue: '-',
          sortInNumber: true,
        },
        {
          id: 'participant',
          title: this.$$t('participants'),
          sortProp: 'login',
          // sortInUnicode: false, // default
        },
        {
          id: 'kills',
          title: this.$$t('kills'),
          width: '1%',
          defaultValue: '-',
          sortInNumber: true,
        },
        {
          id: 'points',
          title: this.$$t('points'),
          width: '1%',
          defaultValue: '-',
          sortInNumber: true,
        },
        {
          id: 'attachments',
          title: '',
          width: '4.25rem',
          notSort: true,
        },
      ];
    },
    rows() {
      return this.prepareRows(this.players);
    },
    pointsPerKill() {
      return (
        this.$lodash.get(
          this.currentTournamentStage,
          'block_configuration.points_per_kill'
        ) || 1
      );
    },
    pointsPerPlace() {
      return (
        this.$lodash.get(
          this.currentTournamentStage,
          'block_configuration.points_per_place'
        ) || null
      );
    },
    battleId() {
      return this.$lodash.get(this, 'editResultsData.battle_id') || null;
    },
  },
  watch: {
    canEdit(newVal) {
      if (!newVal) {
        if (this.$lodash.isEmpty(this.editResultsData)) {
          this.resetTable();
        } else {
          this.saveTable();
        }
        this.activeRowId = null;
        this.$nextTick(this.applyLastSort);
      } else {
        this.getResults(this.editResultsData);
      }
    },
  },
  mounted() {
    this.checkEqualsRank();
  },
  methods: {
    ...mapActions(['getBattleResults', 'errorNotify']),
    onDeletePlayer(id, index) {
      this.$emit('deletePlayer', {
        playerID: id,
        playerIndex: index,
      });
    },
    onAddPlayer(event) {
      if (this.canEditPlayers && this.$route.meta.showAdminFunctions) {
        this.$emit('addPlayer', {
          event,
        });
      }
    },
    getResults({ battle_id: battleID }) {
      return this.getBattleResults({
        battleID,
        isBattleRoyale: true,
      })
        .then(({ match_results: { players_results: results } }) => {
          this.results = results;
        })
        .catch(this.errorNotify);
    },
    prepareRows(players) {
      return players.map(player => {
        let playerModel = {
          id: null,
          participant: {
            name: '-',
            type: 'profile',
          },
          kills: null,
          points: null,
          rank: null,
        };
        if (player) {
          const { profile, score } = player;
          const id = profile.login || profile.id;
          const trIsActive =
            (score.rank !== null &&
              players
                .filter(player => profile !== player.profile)
                .some(({ score: { rank } }) => rank === score.rank)) ||
            this.activeRowId === id;
          playerModel = {
            ...playerModel,
            id,
            participant: profile,
            ...(this.ignoreScores ? {} : score),
            binds: {
              tr: {
                class: trIsActive ? 'active' : '',
              },
            },
          };
        }
        return playerModel;
      });
    },
    focusToNextInput(event = null) {
      const tabIndex = this.$lodash.get(event, 'target.tabIndex') || -1;
      const nextInput = this.$el.querySelector(
        tabIndex === -1 ? `input` : `[tabindex="${tabIndex + 1}"]`
      );

      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    },
    isChanged(row, prop) {
      const rowBuff = this.rowsBuff.find(({ id }) => id === row.id);
      return +rowBuff[prop] !== +row[prop];
    },
    isDoubled(row, prop) {
      return this.rows.some(
        r => r[prop] !== null && r[prop] === row[prop] && r.id !== row.id
      );
    },
    getDoubledRankPlayersNames(rankValue) {
      return this.rows
        .filter(({ rank }) => rank === rankValue)
        .map(({ participant: { name } }) => name)
        .join(', ');
    },
    isErrorRank({ rank }) {
      return rank > this.rows.length || rank < 1 || !Number.isSafeInteger(rank);
    },
    inputProps(customs = null) {
      return Object.assign(
        {
          class: 'hide-arrows',
          type: 'number',
          step: 1,
          min: 1,
          required: true,
          tabindex: tabindex++,
        },
        customs
      );
    },
    onChange(row, prop, value) {
      const { id } = row;

      this.$set(
        this.players.find(({ profile }) => (profile.login || profile.id) === id)
          .score,
        prop,
        +value
      );
      const players = this.$lodash.get(this, 'editResultsData.players', null);
      if (players) {
        players.forEach(v => {
          if ((v.profile.login || v.profile.id) === id) {
            v.score = Object.assign(v.score, { [prop]: +value });
            v.isChanged = true; // use in provideBattleResultsByAdminInBattleRoyale
          }
        });
      }
    },
    onFocus(row) {
      const { id } = row;
      if (!this.canEdit || this.activeRowId === id || !this.results) {
        return;
      }

      this.activeRowId = id;
    },
    resetTable() {
      this.rowsBuff.forEach(({ rank, kills }, i) => {
        this.$set(this.rows[i], 'rank', rank);
        this.$set(this.rows[i], 'kills', kills);
      });
    },
    saveTable() {
      this.rowsBuff = this.$lodash.cloneDeep(this.rows);
      this.checkEqualsRank();
    },
    saveLastSort({ sortBy, order }) {
      this.sortBy = sortBy;
      this.sortOrder = order;
    },
    applyLastSort() {
      const { sortTable } = this.$refs;

      if (!sortTable || !this.sortBy) {
        return;
      }

      sortTable.sortBy(this.sortBy, -1, this.sortOrder);
    },
    checkEqualsRank() {
      this.$nextTick(() => {
        const settedRows = this.rowsBuff.filter(({ rank }) =>
          Number.isSafeInteger(rank)
        );
        this.$emit(
          'toggleEqualsRanks',
          this.$lodash.uniqBy(settedRows, 'rank').length !== settedRows.length
        );
      });
    },
  },
};
</script>

<style lang="scss">
.tournament-grid-players-table {
  .input-plain-text {
    > input {
      padding-right: 0.5rem;
      padding-left: 0.5rem;
      &:focus {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
    &.changed:after {
      content: '*';
    }
    &.has-error,
    &.doubling {
      input {
        color: $ph-error;
      }
    }
  }
  &.is-edit {
    input {
      background-color: rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(0, 0, 0, 0.2);
    }
  }
  .hera-table {
    /*border-spacing: 0;*/
    .thead-main {
      background-color: var(--ph-input-bg) !important;
    }
    td,
    th {
      padding: 0.2rem !important;
    }

    tr {
      &.active {
        background-color: $ph-card-raw !important;
        > td {
          background-color: transparent;
        }
      }
      td {
        text-align: center;
        padding-top: $ph-small-space !important;
        padding-bottom: $ph-small-space !important;

        .participant-wrapper {
          justify-content: start !important;
        }
      }
    }
  }
}
.match-attachments-tooltip {
  min-width: auto;
}
</style>
